import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Trash2, Pencil, Loader2, X, GitFork, Send, Search } from 'lucide-react';
import { API } from '../../App';

export default function PlaybookLibrary({ wsId }) {
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', category: '', prompt_template: '', department: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('published');
  const [showFork, setShowFork] = useState(false);
  const [forkSearch, setForkSearch] = useState('');
  const [forkResults, setForkResults] = useState([]);
  const [forkLoading, setForkLoading] = useState(false);

  const load = () => {
    if (!wsId) return;
    setLoading(true);
    API.get(`/api/workspaces/${wsId}/enterprise-playbooks?status=${filter}`)
      .then(r => setPlaybooks(r.data.playbooks || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [wsId, filter]);

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await API.put(`/api/workspaces/${wsId}/enterprise-playbooks/${editing}`, form);
      } else {
        await API.post(`/api/workspaces/${wsId}/enterprise-playbooks`, form);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', description: '', category: '', prompt_template: '', department: '' });
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed');
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this playbook?')) return;
    await API.delete(`/api/workspaces/${wsId}/enterprise-playbooks/${id}`);
    load();
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ name: p.name || '', description: p.description || '', category: p.category || '', prompt_template: p.prompt_template || '', department: p.department || '' });
    setShowForm(true);
  };

  const submitForReview = async (id) => {
    try {
      await API.post(`/api/workspaces/${wsId}/content/submit`, { content_type: 'playbook', content_id: id });
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Submit failed');
    }
  };

  const forkFromMarketplace = async (playbookId) => {
    setError('');
    try {
      await API.post(`/api/workspaces/${wsId}/enterprise-playbooks/fork`, { playbook_id: playbookId });
      setShowFork(false);
      setForkSearch('');
      setForkResults([]);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Fork failed');
    }
  };

  const searchMarketplace = async () => {
    if (!forkSearch.trim()) return;
    setForkLoading(true);
    try {
      const r = await API.get(`/api/playbooks?q=${encodeURIComponent(forkSearch.trim())}&limit=10`);
      setForkResults(r.data.playbooks || r.data || []);
    } catch {
      setForkResults([]);
    }
    setForkLoading(false);
  };

  if (loading) return <div className="flex items-center gap-2 text-[#A0A0A0] py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading playbooks...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          {['published', 'draft', 'in_review'].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1.5 text-xs font-mono min-h-[44px] ${filter === s ? 'bg-[#FF4D00] text-black' : 'bg-[#0F0F0F] border border-[#2A2A2A] text-[#A0A0A0]'}`}>
              {s.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowFork(!showFork)}
            className="px-4 py-2 bg-[#0F0F0F] border border-[#FF4D00]/30 text-[#FF4D00] text-sm font-medium flex items-center gap-2 min-h-[44px]">
            <GitFork className="w-4 h-4" /> Fork from Marketplace
          </button>
          <button onClick={() => { setShowForm(true); setEditing(null); setForm({ name: '', description: '', category: '', prompt_template: '', department: '' }); }}
            className="px-4 py-2 bg-[#FF4D00] text-black text-sm font-medium flex items-center gap-2 min-h-[44px]">
            <Plus className="w-4 h-4" /> New Playbook
          </button>
        </div>
      </div>

      {showForm && (
        <div className="p-4 bg-[#0F0F0F] border border-[#FF4D00]/30 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold">{editing ? 'Edit' : 'New'} Playbook</h3>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="Name" className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" />
          <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
            placeholder="Category" className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" />
          <input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })}
            placeholder="Department" className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" />
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            placeholder="Description" rows={2} className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00] resize-none" />
          <textarea value={form.prompt_template} onChange={e => setForm({ ...form, prompt_template: e.target.value })}
            placeholder="Prompt template" rows={3} className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm font-mono focus:outline-none focus:border-[#FF4D00] resize-none" />
          {error && <p className="text-[#EF4444] text-xs">{error}</p>}
          <button onClick={save} disabled={saving || !form.name.trim()}
            className="px-4 py-2 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50 min-h-[44px]">
            {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
          </button>
        </div>
      )}

      {showFork && (
        <div className="p-4 bg-[#0F0F0F] border border-[#FF4D00]/30 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold">Fork from Marketplace</h3>
            <button onClick={() => setShowFork(false)} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-2">
            <input value={forkSearch} onChange={e => setForkSearch(e.target.value)}
              placeholder="Search playbooks..." onKeyDown={e => e.key === 'Enter' && searchMarketplace()}
              className="flex-1 px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" />
            <button onClick={searchMarketplace} disabled={forkLoading || !forkSearch.trim()}
              className="px-4 py-2 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50 flex items-center gap-2 min-h-[44px]">
              <Search className="w-4 h-4" /> Search
            </button>
          </div>
          {forkLoading && <div className="flex items-center gap-2 text-[#A0A0A0] text-xs"><Loader2 className="w-3 h-3 animate-spin" /> Searching...</div>}
          {forkResults.length > 0 && (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {forkResults.map(p => (
                <div key={p.id} className="flex items-center gap-3 p-2 bg-[#1A1A1A] border border-[#2A2A2A]">
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium truncate block">{p.name}</span>
                    {p.category && <span className="text-xs text-[#A0A0A0]">{p.category}</span>}
                  </div>
                  <button onClick={() => forkFromMarketplace(p.id)}
                    className="px-3 py-1.5 bg-[#FF4D00] text-black text-xs font-medium flex items-center gap-1 min-h-[44px]">
                    <GitFork className="w-3 h-3" /> Fork
                  </button>
                </div>
              ))}
            </div>
          )}
          {!forkLoading && forkSearch && forkResults.length === 0 && (
            <p className="text-xs text-[#A0A0A0]">No playbooks found. Try a different search.</p>
          )}
        </div>
      )}

      {playbooks.length === 0 ? (
        <div className="text-center py-8 text-[#A0A0A0]">
          <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No playbooks with status "{filter}"</p>
        </div>
      ) : (
        <div className="space-y-2">
          {playbooks.map(p => (
            <div key={p.id} className="flex items-center gap-3 p-3 bg-[#0F0F0F] border border-[#2A2A2A]">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{p.name}</span>
                  {p.category && <span className="text-xs px-1.5 py-0.5 bg-[#2A2A2A] text-[#A0A0A0]">{p.category}</span>}
                </div>
                {p.description && <p className="text-xs text-[#A0A0A0] truncate">{p.description}</p>}
                {p.department && <span className="text-xs font-mono text-[#FF4D00]/60">{p.department}</span>}
              </div>
              {(p.status === 'draft' || p.status === 'changes_requested') && (
                <button onClick={() => submitForReview(p.id)} title="Submit for Review"
                  className="p-2 text-[#A0A0A0] hover:text-[#22C55E] min-w-[44px] min-h-[44px] flex items-center justify-center">
                  <Send className="w-4 h-4" />
                </button>
              )}
              <button onClick={() => startEdit(p)} className="p-2 text-[#A0A0A0] hover:text-[#FF4D00] min-w-[44px] min-h-[44px] flex items-center justify-center">
                <Pencil className="w-4 h-4" />
              </button>
              <button onClick={() => remove(p.id)} className="p-2 text-[#A0A0A0] hover:text-[#EF4444] min-w-[44px] min-h-[44px] flex items-center justify-center">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
